<template>
  <div class="page">

    <!-- HEADER -->
    <div class="page-header">
      <div>
        <h2>Modules</h2>
        <p>Manage course modules and their content.</p>
      </div>

      <button class="btn-primary" @click="openForm()">
        + Add Module
      </button>
    </div>


    <!-- TOOLBAR -->
    <div class="toolbar">

      <input
        v-model="search"
        placeholder="Search module..."
      />

      <select v-model="filterCourse" class="filter-select">
        <option value="">All Courses</option>

        <option
          v-for="course in courses"
          :key="course.course_id"
          :value="course.course_id"
        >
          {{ course.title }}
        </option>
      </select>

      <select v-model="filterType" class="filter-select">
        <option value="">All Types</option>
        <option value="module">Module</option>
        <option value="section">Section</option>
        <option value="topic">Topic</option>
      </select>

    </div>


    <!-- TABLE -->
    <div class="card table-card">

      <div class="table-wrapper">

        <table class="modern-table">

          <thead>
            <tr>
              <th>Module</th>
              <th>Course</th>
              <th>Type</th>
              <th>Lectures</th>
              <th>Description</th>
              <th>Created</th>
              <th style="text-align:right;">Actions</th>
            </tr>
          </thead>

          <tbody>

            <tr
              v-for="module in filteredModules"
              :key="module.module_id"
            >

              <!-- MODULE -->
              <td>

                <div class="module-info">

                  <div class="module-icon">
                    <i class="bx bx-layer"></i>
                  </div>

                  <div class="module-name">

                    <strong>
                      {{ module.module_title }}
                    </strong>

                    <small>
                      #{{ module.module_id }}
                    </small>

                  </div>

                </div>

              </td>


              <!-- COURSE -->
              <td>

                <span class="course-badge">
                  {{ module.course_name }}
                </span>

              </td>


              <!-- TYPE -->
              <td>

                <span class="type-badge">
                  {{ module.module_type || "Module" }}
                </span>

              </td>


              <!-- LECTURES -->
              <td>

                <div class="lecture-count">

                  <i class="bx bx-video"></i>

                  {{ module.lecture_count }}

                </div>

              </td>


              <!-- DESCRIPTION -->
              <td>

                <div class="description-cell">

                  {{
                    module.module_desc
                      ? module.module_desc
                      : "-"
                  }}

                </div>

              </td>


              <!-- DATE -->
              <td>

                {{ formatDate(module.created_at) }}

              </td>


              <!-- ACTIONS -->
              <td style="text-align:right;">

                <button
                  class="icon-btn view"
                  title="View"
                  @click="viewModule(module)"
                >
                  <i class="bx bx-show"></i>
                </button>

                <button
                  class="icon-btn edit"
                  title="Edit"
                  @click="openForm(module)"
                >
                  <i class="bx bx-edit"></i>
                </button>

                <button
                  class="icon-btn delete"
                  title="Delete"
                  @click="openDelete(module)"
                >
                  <i class="bx bx-trash"></i>
                </button>

              </td>

            </tr>


            <!-- EMPTY -->
            <tr v-if="filteredModules.length === 0">

              <td colspan="7" class="empty-state">

                <i class="bx bx-layer"></i>

                <h4>No modules found</h4>

                <p>
                  Try changing your search or filters.
                </p>

              </td>

            </tr>

          </tbody>

        </table>

      </div>

    </div>


    <!-- PAGINATION -->
    <div class="pagination">

      <button
        :disabled="page === 0"
        @click="prevPage"
      >
        <i class="bx bx-chevron-left"></i>
        Prev
      </button>

      <span>
        Page {{ page + 1 }}
      </span>

      <button
        :disabled="page >= totalPages - 1"
        @click="nextPage"
      >
        Next
        <i class="bx bx-chevron-right"></i>
      </button>

    </div>


    <!-- ADD / EDIT MODAL -->
    <div
      v-if="showModal"
      class="modal-overlay"
      @click.self="close"
    >

      <div class="modal-box">

        <!-- HEADER -->
        <div class="modal-header">

          <div>

            <h3>
              {{
                form.module_id
                  ? "Edit Module"
                  : "Add Module"
              }}
            </h3>

            <p>
              {{
                form.module_id
                  ? "Update module information."
                  : "Create a new course module."
              }}
            </p>

          </div>

          <button
            class="modal-close"
            @click="close"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>


        <!-- FORM -->
        <div class="form-section">

          <div class="form-group">

            <label>Course *</label>

            <select v-model="form.module_parent">

              <option value="">
                Select Course
              </option>

              <option
                v-for="course in courses"
                :key="course.course_id"
                :value="course.course_id"
              >
                {{ course.title }}
              </option>

            </select>

          </div>


          <div class="form-group">

            <label>Module Title *</label>

            <input
              v-model="form.module_title"
              placeholder="Enter module title"
            />

          </div>


          <div class="form-grid">

            <div class="form-group">

              <label>Module Type</label>

              <select v-model="form.module_type">

                <option value="">
                  Select Type
                </option>

                <option value="module">
                  Module
                </option>

                <option value="section">
                  Section
                </option>

                <option value="topic">
                  Topic
                </option>

              </select>

            </div>


            <div class="form-group">

              <label>Parent Module</label>

              <select v-model="form.module_parent_module">

                <option value="">
                  No Parent
                </option>

                <option
                  v-for="module in modules"
                  :key="module.module_id"
                  :value="module.module_id"
                >
                  {{ module.module_title }}
                </option>

              </select>

            </div>

          </div>


          <div class="form-group">

            <label>Module Image</label>

            <input
              v-model="form.module_img"
              placeholder="Image URL"
            />

          </div>


          <div class="form-group">

            <label>Module Description</label>

            <textarea
              v-model="form.module_desc"
              placeholder="Write module description..."
            ></textarea>

          </div>

        </div>


        <!-- SEO -->
        <div class="form-section">

          <h4>SEO Information</h4>

          <div class="form-group">

            <label>Meta Title</label>

            <input
              v-model="form.module_meta_title"
              placeholder="Meta title"
            />

          </div>


          <div class="form-group">

            <label>Keywords</label>

            <textarea
              v-model="form.module_keyword"
              placeholder="Enter keywords..."
            ></textarea>

          </div>

        </div>


        <!-- ACTIONS -->
        <div class="modal-actions">

          <button
            class="btn-secondary"
            @click="close"
          >
            Cancel
          </button>

          <button
            class="btn-primary"
            @click="save"
          >
            {{
              form.module_id
                ? "Update Module"
                : "Create Module"
            }}
          </button>

        </div>

      </div>

    </div>


    <!-- VIEW MODAL -->
    <div
      v-if="viewModal"
      class="modal-overlay"
      @click.self="viewModal = false"
    >

      <div class="modal-box view-modal">

        <div class="modal-header">

          <div>

            <h3>Module Details</h3>

            <p>
              Module #{{ selectedModule.module_id }}
            </p>

          </div>

          <button
            class="modal-close"
            @click="viewModal = false"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>


        <div class="module-view">

          <div class="large-module-icon">

            <i class="bx bx-layer"></i>

          </div>

          <div>

            <h2>
              {{ selectedModule.module_title }}
            </h2>

            <p>
              {{ selectedModule.module_desc || "No description available." }}
            </p>

          </div>

        </div>


        <div class="detail-grid">

          <div>

            <span>Course</span>

            <strong>
              {{ selectedModule.course_name }}
            </strong>

          </div>


          <div>

            <span>Type</span>

            <strong>
              {{ selectedModule.module_type || "-" }}
            </strong>

          </div>


          <div>

            <span>Lectures</span>

            <strong>
              {{ selectedModule.lecture_count }}
            </strong>

          </div>


          <div>

            <span>Created</span>

            <strong>
              {{ formatDate(selectedModule.created_at) }}
            </strong>

          </div>

        </div>


        <div class="modal-actions">

          <button
            class="btn-secondary"
            @click="viewModal = false"
          >
            Close
          </button>

          <button
            class="btn-primary"
            @click="editFromView"
          >
            <i class="bx bx-edit"></i>
            Edit Module
          </button>

        </div>

      </div>

    </div>


    <!-- DELETE MODAL -->
    <div
      v-if="deleteModal"
      class="modal-overlay"
      @click.self="deleteModal = false"
    >

      <div class="modal-box delete-box">

        <div class="delete-icon">
          <i class="bx bx-trash"></i>
        </div>

        <h3>Delete Module?</h3>

        <p>

          Are you sure you want to delete

          <strong>
            {{ selectedModule?.module_title }}
          </strong>?

          This action cannot be undone.

        </p>


        <div class="modal-actions">

          <button
            class="btn-secondary"
            @click="deleteModal = false"
          >
            Cancel
          </button>

          <button
            class="btn-danger"
            @click="confirmDelete"
          >
            Delete
          </button>

        </div>

      </div>

    </div>

  </div>
</template>


<script>
import {
  getModulesApi,
  getModuleStatsApi,
  getModuleApi,
  createModuleApi,
  updateModuleApi,
  deleteModuleApi,
} from "@/apis/modulecon";

import { getCoursesApi } from "@/apis/courseapis";

export default {
  name: "ModulesScreen",

  data() {
    return {
      search: "",
      filterCourse: "",
      filterType: "",

      page: 0,
      perPage: 6,

      modules: [],
      courses: [],

      totalModules: 0,
      totalPages: 1,

      moduleCount: 0,
      sectionCount: 0,
      topicCount: 0,

      showModal: false,
      viewModal: false,
      deleteModal: false,

      selectedModule: null,
      form: {},

      loading: false,
      saving: false,
      deleting: false,
      coursesLoading: false,
    };
  },

  computed: {
    filteredModules() {
      return this.modules;
    },
  },

  watch: {
    search() {
      this.page = 0;
      this.loadModules();
    },

    filterCourse() {
      this.page = 0;
      this.loadModules();
    },

    filterType() {
      this.page = 0;
      this.loadModules();
    },
  },

  mounted() {
    this.filterCourse = this.$route.query.course_id || "";
    this.loadCourses();
    this.loadModules();
    this.loadStats();
  },

  methods: {
    /*
    |--------------------------------------------------------------------------
    | LOAD COURSES
    |--------------------------------------------------------------------------
    */

    async loadCourses() {
      this.coursesLoading = true;

      try {
        const response = await getCoursesApi({
          page: 1,
          limit: 100,
          search: "",
          course_typ: "",
          category: "",
          creator_id: "",
        });

        if (response?.status) {
          this.courses = response.courses || [];
        } else {
          this.courses = [];
          console.error(
            response?.success || "Courses fetch failed."
          );
        }
      } catch (error) {
        console.error("Load Courses Error:", error);
        this.courses = [];
      } finally {
        this.coursesLoading = false;
      }
    },

    /*
    |--------------------------------------------------------------------------
    | LOAD MODULES
    |--------------------------------------------------------------------------
    */

    async loadModules() {
      this.loading = true;

      try {
        const response = await getModulesApi({
          page: this.page + 1,
          limit: this.perPage,
          search: this.search,
          course_id: this.filterCourse,
          module_type: this.filterType,
        });

        if (response?.status) {
          this.modules = response.modules || [];

          this.totalModules = Number(
            response.total || 0
          );

          this.totalPages = Math.max(
            Number(response.totalPages || 1),
            1
          );

          this.page = Math.max(
            Number(response.page || 1) - 1,
            0
          );
        } else {
          this.modules = [];
          this.totalModules = 0;
          this.totalPages = 1;

          console.error(
            response?.success || "Modules fetch failed."
          );
        }
      } catch (error) {
        console.error("Load Modules Error:", error);

        this.modules = [];
        this.totalModules = 0;
        this.totalPages = 1;
      } finally {
        this.loading = false;
      }
    },

    /*
    |--------------------------------------------------------------------------
    | LOAD STATS
    |--------------------------------------------------------------------------
    */

    async loadStats() {
      try {
        const response = await getModuleStatsApi();

        if (response?.status) {
          this.totalModules = Number(
            response.totalModules || 0
          );

          this.moduleCount = Number(
            response.modules || 0
          );

          this.sectionCount = Number(
            response.sections || 0
          );

          this.topicCount = Number(
            response.topics || 0
          );
        }
      } catch (error) {
        console.error(
          "Load Module Stats Error:",
          error
        );
      }
    },

    /*
    |--------------------------------------------------------------------------
    | ADD / EDIT
    |--------------------------------------------------------------------------
    */

    openForm(module = null) {
      if (module) {
        this.form = {
          ...module,

          module_parent:
            module.module_parent || "",

          module_title:
            module.module_title || "",

          module_meta_title:
            module.module_meta_title || "",

          module_keyword:
            module.module_keyword || "",

          module_desc:
            module.module_desc || "",

          module_type:
            module.module_type || "module",

          module_img:
            module.module_img || "",
        };
      } else {
        this.form = {
          module_parent: "",
          module_title: "",
          module_meta_title: "",
          module_keyword: "",
          module_desc: "",
          module_type: "module",
          module_img: "",
        };
      }

      this.showModal = true;
    },

    close() {
      this.showModal = false;
      this.form = {};
    },

    /*
    |--------------------------------------------------------------------------
    | VIEW MODULE
    |--------------------------------------------------------------------------
    */

    async viewModule(module) {
      if (!module?.module_id) {
        return;
      }

      try {
        const response = await getModuleApi(
          module.module_id
        );

        if (response?.status) {
          this.selectedModule = response.module;
          this.viewModal = true;
        } else {
          alert(
            response?.success ||
              "Module fetch failed."
          );
        }
      } catch (error) {
        console.error(
          "View Module Error:",
          error
        );

        alert(
          "Unable to load module details."
        );
      }
    },

    /*
    |--------------------------------------------------------------------------
    | EDIT FROM VIEW
    |--------------------------------------------------------------------------
    */

    editFromView() {
      const module = this.selectedModule;

      this.viewModal = false;

      if (module) {
        this.openForm(module);
      }
    },

    /*
    |--------------------------------------------------------------------------
    | SAVE
    |--------------------------------------------------------------------------
    */

    async save() {
      const title = String(
        this.form.module_title || ""
      ).trim();

      if (!title) {
        alert("Module title is required.");
        return;
      }

      if (!this.form.module_parent) {
        alert("Please select a course.");
        return;
      }

      const isEdit = !!this.form.module_id;

      this.saving = true;

      try {
        let response;

        if (isEdit) {
          response = await updateModuleApi(
            this.form
          );
        } else {
          response = await createModuleApi(
            this.form
          );
        }

        if (response?.status) {
          this.close();

          await this.loadModules();
          await this.loadStats();

          alert(
            response.success ||
              (
                isEdit
                  ? "Module updated successfully."
                  : "Module created successfully."
              )
          );
        } else {
          alert(
            response?.success ||
              "Module save failed."
          );
        }
      } catch (error) {
        console.error(
          "Save Module Error:",
          error
        );

        alert(
          "Unable to save module."
        );
      } finally {
        this.saving = false;
      }
    },

    /*
    |--------------------------------------------------------------------------
    | DELETE
    |--------------------------------------------------------------------------
    */

    openDelete(module) {
      if (!module?.module_id) {
        return;
      }

      this.selectedModule = module;
      this.deleteModal = true;
    },

    async confirmDelete() {
      if (!this.selectedModule?.module_id) {
        return;
      }

      this.deleting = true;

      try {
        const response = await deleteModuleApi(
          this.selectedModule.module_id
        );

        if (response?.status) {
          this.deleteModal = false;
          this.selectedModule = null;

          if (
            this.modules.length === 1 &&
            this.page > 0
          ) {
            this.page--;
          }

          await this.loadModules();
          await this.loadStats();

          alert(
            response.success ||
              "Module deleted successfully."
          );
        } else {
          alert(
            response?.success ||
              "Module deletion failed."
          );
        }
      } catch (error) {
        console.error(
          "Delete Module Error:",
          error
        );

        alert(
          "Unable to delete module."
        );
      } finally {
        this.deleting = false;
      }
    },

    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    prevPage() {
      if (this.page > 0) {
        this.page--;
        this.loadModules();
      }
    },

    nextPage() {
      if (
        this.page <
        this.totalPages - 1
      ) {
        this.page++;
        this.loadModules();
      }
    },

    /*
    |--------------------------------------------------------------------------
    | DATE
    |--------------------------------------------------------------------------
    */

    formatDate(date) {
      if (!date) {
        return "-";
      }

      return new Intl.DateTimeFormat(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      ).format(new Date(date));
    },
  },
};
</script>


<style scoped>

/* =========================================================
   PAGE
========================================================= */

.page {
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
}


/* =========================================================
   HEADER
========================================================= */

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
}

.page-header h2 {
  margin: 0;
  color: #0F172A;
  font-size: 24px;
  font-weight: 700;
}

.page-header p {
  margin: 5px 0 0;
  color: #64748B;
  font-size: 12px;
}


/* =========================================================
   BUTTONS
========================================================= */

.btn-primary {
  height: 40px;
  padding: 0 15px;
  border: none;
  border-radius: 9px;
  background: #2563EB;
  color: white;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-primary:hover {
  background: #1D4ED8;
}

.btn-secondary {
  height: 40px;
  padding: 0 15px;
  border: 1px solid #E2E8F0;
  border-radius: 9px;
  background: white;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.btn-danger {
  height: 40px;
  padding: 0 15px;
  border: none;
  border-radius: 9px;
  background: #EF4444;
  color: white;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}


/* =========================================================
   TOOLBAR
========================================================= */

.toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.toolbar input,
.filter-select {
  height: 40px;
  padding: 0 12px;
  border: 1px solid #E2E8F0;
  border-radius: 9px;
  background: white;
  color: #334155;
  font-size: 12px;
  outline: none;
}

.toolbar input {
  width: 280px;
}

.filter-select {
  min-width: 170px;
}

.toolbar input:focus,
.filter-select:focus {
  border-color: #2563EB;
}


/* =========================================================
   CARD
========================================================= */

.card {
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 15px;
  overflow: hidden;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
}


/* =========================================================
   TABLE
========================================================= */

.modern-table {
  width: 100%;
  min-width: 1000px;
  border-collapse: collapse;
}

.modern-table th {
  padding: 13px 14px;
  background: #F8FAFC;
  border-bottom: 1px solid #E2E8F0;
  color: #64748B;
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .3px;
  white-space: nowrap;
}

.modern-table td {
  padding: 13px 14px;
  border-bottom: 1px solid #F1F5F9;
  color: #334155;
  font-size: 12px;
  white-space: nowrap;
}

.modern-table tbody tr:hover {
  background: #FAFCFF;
}


/* =========================================================
   MODULE
========================================================= */

.module-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 230px;
}

.module-icon {
  width: 40px;
  height: 40px;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: #EFF6FF;
  color: #2563EB;
  font-size: 18px;
}

.module-name strong {
  display: block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #0F172A;
  font-size: 12px;
  font-weight: 700;
}

.module-name small {
  display: block;
  margin-top: 3px;
  color: #94A3B8;
  font-size: 9px;
}


/* =========================================================
   BADGES
========================================================= */

.course-badge,
.type-badge {
  display: inline-flex;
  align-items: center;
  max-width: 210px;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 5px 9px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
}

.course-badge {
  background: #F1F5F9;
  color: #475569;
}

.type-badge {
  background: #F5F3FF;
  color: #7C3AED;
}


/* =========================================================
   LECTURES
========================================================= */

.lecture-count {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #475569;
  font-weight: 600;
}

.lecture-count i {
  color: #2563EB;
  font-size: 15px;
}


/* =========================================================
   DESCRIPTION
========================================================= */

.description-cell {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #64748B;
  font-size: 11px;
}


/* =========================================================
   ACTIONS
========================================================= */

.icon-btn {
  width: 31px;
  height: 31px;
  border: none;
  border-radius: 7px;
  margin-left: 5px;
  cursor: pointer;
  font-size: 16px;
}

.icon-btn.view {
  background: #F1F5F9;
  color: #475569;
}

.icon-btn.edit {
  background: #EFF6FF;
  color: #2563EB;
}

.icon-btn.delete {
  background: #FEF2F2;
  color: #EF4444;
}

.icon-btn:hover {
  transform: translateY(-1px);
}


/* =========================================================
   EMPTY
========================================================= */

.empty-state {
  padding: 50px !important;
  text-align: center !important;
}

.empty-state i {
  color: #94A3B8;
  font-size: 35px;
}

.empty-state h4 {
  margin: 8px 0 3px;
  color: #334155;
}

.empty-state p {
  margin: 0;
  color: #94A3B8;
  font-size: 11px;
}


/* =========================================================
   PAGINATION
========================================================= */

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
}

.pagination button {
  height: 34px;
  padding: 0 12px;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  background: white;
  color: #334155;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}

.pagination button:disabled {
  opacity: .45;
  cursor: not-allowed;
}

.pagination span {
  color: #64748B;
  font-size: 11px;
}


/* =========================================================
   MODAL
========================================================= */

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, .5);
  backdrop-filter: blur(4px);
}

.modal-box {
  width: 100%;
  max-width: 620px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 22px;
  border-radius: 16px;
  background: white;
  box-shadow: 0 25px 60px rgba(15, 23, 42, .18);
}

.view-modal {
  max-width: 650px;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 20px;
}

.modal-header h3 {
  margin: 0;
  color: #0F172A;
  font-size: 18px;
  font-weight: 700;
}

.modal-header p {
  margin: 4px 0 0;
  color: #94A3B8;
  font-size: 11px;
}

.modal-close {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #F8FAFC;
  color: #64748B;
  cursor: pointer;
  font-size: 18px;
}


/* =========================================================
   FORM
========================================================= */

.form-section {
  padding: 18px 0;
  border-top: 1px solid #F1F5F9;
}

.form-section:first-of-type {
  border-top: none;
  padding-top: 0;
}

.form-section h4 {
  margin: 0 0 14px;
  color: #334155;
  font-size: 13px;
  font-weight: 700;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 13px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 13px;
}

.form-group label {
  color: #475569;
  font-size: 10px;
  font-weight: 700;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 11px;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  background: white;
  color: #334155;
  font-size: 11px;
  outline: none;
  font-family: inherit;
}

.form-group textarea {
  min-height: 90px;
  resize: vertical;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: #2563EB;
}


/* =========================================================
   MODULE VIEW
========================================================= */

.module-view {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 20px;
}

.large-module-icon {
  width: 60px;
  height: 60px;
  min-width: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: #EFF6FF;
  color: #2563EB;
  font-size: 28px;
}

.module-view h2 {
  margin: 0 0 7px;
  color: #0F172A;
  font-size: 19px;
}

.module-view p {
  margin: 0;
  color: #64748B;
  font-size: 11px;
  line-height: 1.6;
}


/* =========================================================
   DETAILS
========================================================= */

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.detail-grid div {
  padding: 11px;
  border-radius: 8px;
  background: #F8FAFC;
}

.detail-grid span {
  display: block;
  color: #94A3B8;
  font-size: 9px;
}

.detail-grid strong {
  display: block;
  margin-top: 3px;
  color: #334155;
  font-size: 11px;
}


/* =========================================================
   MODAL ACTIONS
========================================================= */

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 9px;
  padding-top: 18px;
  margin-top: 5px;
  border-top: 1px solid #F1F5F9;
}


/* =========================================================
   DELETE
========================================================= */

.delete-box {
  max-width: 360px;
  text-align: center;
}

.delete-icon {
  width: 55px;
  height: 55px;
  margin: 0 auto 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: #FEF2F2;
  color: #EF4444;
  font-size: 25px;
}

.delete-box h3 {
  margin: 0;
  color: #0F172A;
  font-size: 17px;
}

.delete-box p {
  margin: 7px 0 0;
  color: #64748B;
  font-size: 11px;
  line-height: 1.5;
}


/* =========================================================
   RESPONSIVE
========================================================= */

@media (max-width: 768px) {

  .page {
    padding: 15px;
  }

  .page-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar input,
  .filter-select {
    width: 100%;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .module-view {
    flex-direction: column;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

}


@media (max-width: 480px) {

  .page {
    padding: 12px;
  }

  .page-header h2 {
    font-size: 21px;
  }

  .modal-overlay {
    padding: 10px;
  }

  .modal-box {
    padding: 17px;
  }

}

</style>